import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import AddIcon from "@mui/icons-material/Add";
import PersonIcon from "@mui/icons-material/Person";
import RouteIcon from "@mui/icons-material/Route";
import IconButton from "@mui/material/IconButton";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import PaletteIcon from "@mui/icons-material/Palette";
import Tooltip from "@mui/material/Tooltip";
import DayBookingDialog from "./DayBookingDialog";
import { TimelineGrid } from "../timelinePlanner/timelinePlannerGrid";
import { PlannedWorkShiftDto } from "../../models/plannedWorkShift/plannedWorkShift";
import { Base } from "../../framework/base";
import {
    WorkShiftPlanningParams,
    WorkShiftPlanningTab,
} from "./WorkShiftPlanningRouter";
import { selectColorSchemeByScope } from "../../store/colorSchemeSlice";
import { VehicleListItemDto } from "../../models/transport/vehicle";
import { getVehicles } from "../../services/vehicleService";
import { WorkShiftTimelineRow } from "./WorkShiftTimelineRow";
import { EmployeeListItemDto } from "../../models/employee/employee";
import { showApiError } from "../framework/formUtils";
import { LoadingIndicator } from "../framework/loadingIndicatorNew";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { getRoutes } from "../../services/routeService";
import { RouteDto } from "../../models/route/route";
import { useAppDispatch, useAppSelector } from "../../framework/customStore";
import { savePlannedWorkShift } from "../../services/plannedWorkShiftService";
import { pasteRecurringInstances, RecurringShiftPasteRequestDto } from "../../services/recurringShiftService";
import { reloadPlannedWorkShifts, setAllRowsExpanded, fetchDayBookings } from "../../store/workShiftPlanningSlice";
import { useCopyPasteOperations } from "../../hooks/timelineGrid/useCopyPasteOperations";
import { useKeyboardShortcuts } from "../../hooks/timelineGrid/useKeyboardShortcuts";
import { useMultiSelect } from "../../hooks/timelineGrid/useMultiSelect";
import { useSingleCellSelect } from "../../hooks/timelineGrid/useSingleCellSelect";
import { getRecurrenceDeleteAction } from "./WorkShiftTimelineCell";
import { RecurrenceAction } from "../../models/common/enums";
import { IMuiMenuItemProps } from "../framework/muiMenu";
import { useAppOptionValueEquals } from "../../hooks/useAppOption";
import { OptionRoutesEnabled } from "../../models/common/consts";
import { DayBookingItemBeta } from "../../models/dayBooking/dayBookingItemBeta";
import { getAbsenceSalaryRowTypeKindCodes } from "../../models/workTime/workTimeList";
import { WorkShiftDtoBasic } from "../../models/workShift/workShift";
import { getBasicWorkShifts } from "../../services/workShiftService";

interface SelectedCell {
    day: string;
    targetId: string;
}

interface WorkShiftTimelineGridProps {
    employees: EmployeeListItemDto[];
    shifts: PlannedWorkShiftDto[];
    timelineStart: string;
    timelineEnd: string;
}

export const WorkShiftTimelineGrid = ({
    shifts,
    timelineStart,
    timelineEnd,
    employees,
}: WorkShiftTimelineGridProps) => {
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { t } = useTranslation();
    const { tab } = useParams<WorkShiftPlanningParams>();
    const { pathname } = useLocation();

    const [vehicles, setVehicles] = useState<VehicleListItemDto[]>();
    const [routes, setRoutes] = useState<RouteDto[]>();

    const todayStr = Base.dayjsToJsonDate();
    const isFuturePayPeriod = timelineStart > todayStr;

    const colorSchemeEnabled = useAppSelector(selectColorSchemeByScope("workShiftPlanning")).enabled;

    const routesReloadKey = useAppSelector(
        (state) => state.workShiftPlanning.routesReloadKey
    );
    const [routesEnabled] = useAppOptionValueEquals(OptionRoutesEnabled, true);

    const dayBookingsByEmployeeId = useAppSelector(
        (state) => state.workShiftPlanning.dayBookingsByEmployeeId
    );
    const dayBookingTypes = useAppSelector(
        (state) => state.workShiftPlanning.dayBookingTypes
    );
    const showAllDayBookings = useAppSelector(
        (state) => !!state.workShiftPlanning.filters.showAllDayBookings
    );

    const absenceKindCodes = useMemo(
        () => getAbsenceSalaryRowTypeKindCodes(),
        []
    );

    const filteredDayBookingsByEmployeeId = useMemo(() => {
        if (showAllDayBookings) {
            return dayBookingsByEmployeeId;
        }

        const result: Record<string, DayBookingItemBeta[]> = {};

        Object.entries(dayBookingsByEmployeeId ?? {}).forEach(([employeeId, bookings]) => {
            result[employeeId] = bookings?.filter((booking) => {
                const type = dayBookingTypes?.[booking.dayBookingTypeId];
                if (!type?.salaryRowTypeKindCode) return false;
                return absenceKindCodes.includes(type?.salaryRowTypeKindCode);
            });
        });

        return result;
    }, [
        dayBookingsByEmployeeId,
        dayBookingTypes,
        showAllDayBookings,
        absenceKindCodes,
    ]);

    const shiftGroupKey = (shift: PlannedWorkShiftDto) => {
        if (tab === WorkShiftPlanningTab.Vehicle) return shift.vehicleId || "null";
        if (tab === WorkShiftPlanningTab.Route) return shift.routeId || "null";
        return shift.employeeId || "null";
    };

    const groupedShifts = useMemo(() => {
        return Base.groupArray(
            shifts,
            tab === WorkShiftPlanningTab.Vehicle
                ? "vehicleId"
                : tab === WorkShiftPlanningTab.Route
                    ? "routeId"
                    : "employeeId"
        );
    }, [shifts, tab]);

    const sortedGroupedShifts = useMemo(() => {
        const result: Record<string, PlannedWorkShiftDto[]> = {};
        const comparator = (a: PlannedWorkShiftDto, b: PlannedWorkShiftDto) => {
            const aStart = a?.startTime ?? "";
            const bStart = b?.startTime ?? "";
            if (aStart !== bStart) return aStart.localeCompare(bStart);
            return 0;
        };

        for (const key of Object.keys(groupedShifts || {})) {
            const list = groupedShifts[key] || [];
            result[key] = [...list].sort(comparator);
        }
        return result;
    }, [groupedShifts]);

    const multiSelect = useMultiSelect<PlannedWorkShiftDto>({
        getItemKey: (shift) => shift.id,
        getItemGroup: shiftGroupKey,
        groupedItems: sortedGroupedShifts,
    });

    const cellSelect = useSingleCellSelect<SelectedCell>({
        onClearOtherSelections: () => multiSelect.clearSelection(),
    });

    const copyPasteConfig = useMemo(() => ({
        getSelectedItems: (selectedIds: string[]) =>
            shifts.filter(s => selectedIds.includes(s.id)),

        saveItems: async(items: PlannedWorkShiftDto[]) => {
            await Promise.all(items.map(savePlannedWorkShift));
            dispatch(reloadPlannedWorkShifts());
        },

        deleteItems: async(items: PlannedWorkShiftDto[]) => {
            await Promise.all(
                items.map((s) =>
                    getRecurrenceDeleteAction(
                        !!s.recurrence ? RecurrenceAction.OnlyThis : null,
                        s
                    )
                )
            );
            dispatch(reloadPlannedWorkShifts());
        },

        // Called for cut+paste when we want to perform save+delete atomically for recurring instances.
        saveAndDeleteItems: async(transformedItems: PlannedWorkShiftDto[], originalItems: PlannedWorkShiftDto[]) => {
            const nonRecurringSaves: Promise<any>[] = [];
            const recurringGroups: Record<string, { rowId: string; ops: any[] }> = {};

            for (let i = 0; i < originalItems.length; i++) {
                const orig = originalItems[i];
                const newShift = transformedItems[i];

                if (orig.recurrence && orig.recurrence.recurringShiftId) {
                    const recurringId = orig.recurrence.recurringShiftId;
                    if (!recurringGroups[recurringId]) {
                        recurringGroups[recurringId] = { rowId: orig.recurrence.rowId, ops: [] };
                    }

                    recurringGroups[recurringId].ops.push({
                        date: orig.startTime.substring(0, 10),
                        newShift: {
                            employeeId: newShift.employeeId,
                            vehicleId: newShift.vehicleId,
                            routeId: newShift.routeId,
                            startTime: newShift.startTime,
                            endTime: newShift.endTime,
                            label: newShift.label,
                            comment: newShift.comment,
                        },
                        deleteOriginalInstance: true,
                        instanceType: 0,
                    });
                } else {
                    nonRecurringSaves.push(savePlannedWorkShift(newShift));
                }
            }

            await Promise.all(nonRecurringSaves);

            const groupCalls: Promise<any>[] = Object.keys(recurringGroups).map(async(recurringId) => {
                const group = recurringGroups[recurringId];
                const dto: RecurringShiftPasteRequestDto = {
                    recurringShiftId: recurringId,
                    recurringShiftRowId: group.rowId,
                    operations: group.ops.map((o) => ({
                        date: o.date,
                        newShift: o.newShift,
                        deleteOriginalInstance: o.deleteOriginalInstance,
                        instanceType: o.instanceType,
                    })),
                };

                await pasteRecurringInstances(dto);
            });

            await Promise.all(groupCalls);
            dispatch(reloadPlannedWorkShifts());
        },

        transformItemsForPaste: (items: PlannedWorkShiftDto[], target: SelectedCell, metadata?: Record<string, any>) => {
            const { minDay } = metadata || {};
            const targetDay = target.day;
            const minDate = minDay ? Base.dayjsParse(minDay as string, "YYYY-MM-DD") : null;
            const targetDate = Base.dayjsParse(targetDay, "YYYY-MM-DD");

            return items.map((s) => {
                const origStartTime = s.startTime;
                const origEndTime = s.endTime;
                const origStartDay = origStartTime.substring(0, 10);
                const origEndDay = origEndTime.substring(0, 10);
                const origStartDate = Base.dayjsParse(origStartDay, "YYYY-MM-DD");
                const origEndDate = Base.dayjsParse(origEndDay, "YYYY-MM-DD");

                const shiftOffset = minDate ? origStartDate.diff(minDate, "day") : 0;
                const durationDays = origEndDate.diff(origStartDate, "day");
                const newStartDate = Base.dateAddDays(targetDate, shiftOffset);
                const newEndDate = Base.dateAddDays(newStartDate, durationDays);
                const newStartDayStr = Base.dayjsToJsonDate(newStartDate);
                const newEndDayStr = Base.dayjsToJsonDate(newEndDate);

                const convertNullString = (value: string): string | null => {
                    return value === "null" ? null : value;
                };

                return {
                    employeeId: tab === WorkShiftPlanningTab.Employee ? convertNullString(target.targetId) : s.employeeId,
                    vehicleId: tab === WorkShiftPlanningTab.Vehicle ? convertNullString(target.targetId) : s.vehicleId,
                    routeId: tab === WorkShiftPlanningTab.Route ? convertNullString(target.targetId) : s.routeId,
                    startTime: newStartDayStr + origStartTime.slice(10),
                    endTime: newEndDayStr + origEndTime.slice(10),
                    label: s.label,
                    comment: s.comment,
                } as PlannedWorkShiftDto;
            });
        },

        extractMetadata: (items: PlannedWorkShiftDto[]) => {
            const minDay = items.reduce((min, s) => {
                const day = s.startTime.substring(0, 10);
                return !min || day < min ? day : min;
            }, "");
            return { minDay };
        },

        onPaste: () => {
            cellSelect.clearSelection();
            multiSelect.clearSelection();
        },

        onError: (error: any) => {
            console.error("Copy/Paste operation failed:", error);
            showApiError(error);
        }
    }), [shifts, tab, dispatch, cellSelect, multiSelect]);

    const {
        handleCopy,
        handleCut,
        handlePaste,
    } = useCopyPasteOperations<PlannedWorkShiftDto, SelectedCell>(copyPasteConfig);

    useKeyboardShortcuts([
        {
            key: "c",
            ctrlKey: true,
            action: () => handleCopy(multiSelect.selectedKeys)
        },
        {
            key: "x",
            ctrlKey: true,
            action: () => handleCut(multiSelect.selectedKeys)
        },
        {
            key: "v",
            ctrlKey: true,
            action: () => {
                if (cellSelect.selectedCell) {
                    void handlePaste(cellSelect.selectedCell);
                }
            }
        }
    ]);

    const isEmployeeTab = tab === WorkShiftPlanningTab.Employee;
    const isVehicleTab = tab === WorkShiftPlanningTab.Vehicle;
    const isRouteTab = tab === WorkShiftPlanningTab.Route;

    const [actualWorkShiftsByEmployee, setActualWorkShiftsByEmployee] = useState<Record<string, WorkShiftDtoBasic[]>>({});

    useEffect(() => {
        if (!isEmployeeTab || !employees?.length) return;

        if (isFuturePayPeriod) {
            setActualWorkShiftsByEmployee({});
            return;
        }

        const fetchActualWorkShifts = async() => {
            try {
                const employeeIds = employees.map(e => e.id);
                const res = await getBasicWorkShifts(
                    employeeIds,
                    timelineStart,
                    timelineEnd
                );

                const grouped = Base.groupArray(res.data, "employeeId");
                setActualWorkShiftsByEmployee(grouped);
            } catch (err) {
                showApiError(err);
                setActualWorkShiftsByEmployee({});
            }
        };
        void fetchActualWorkShifts();
    }, [isEmployeeTab, employees, timelineStart, timelineEnd]);

    useEffect(() => {
        if (isVehicleTab) {
            getVehicles().then((res) => setVehicles(res?.data)).catch(showApiError);
        }
    }, [isEmployeeTab, isVehicleTab]);

    useEffect(() => {
        if (isRouteTab) {
            getRoutes().then(setRoutes).catch(showApiError);
        }
    }, [isRouteTab, routesReloadKey]);

    const loading =
        (isVehicleTab && !vehicles) ||
        (isEmployeeTab && !employees) ||
        (isRouteTab && !routes);

    const handleAddShift = () => {
        history.push(`${pathname}/new/${timelineStart}`);
    };
    const handleAddRoute = () => {
        history.push(`${pathname}/route/new`);
    };

    const [dayBookingDialogOpen, setDayBookingDialogOpen] = useState(false);
    const openDayBookingDialog = () => setDayBookingDialogOpen(true);
    const closeDayBookingDialog = () => setDayBookingDialogOpen(false);

    const addActions: IMuiMenuItemProps[] = [
        {
            label: t("workShiftPlanning.newShift"),
            icon: <AddIcon />,
            onClick: handleAddShift,
        },
        {
            label: t("common.newDayBooking"),
            icon: <AddIcon />,
            onClick: openDayBookingDialog,
        },
        ...(routesEnabled
            ? [{
                label: t("route.newRoute"),
                icon: <AddIcon />,
                onClick: handleAddRoute,
            }]
            : []),
    ];

    const headerActions: React.ReactNode = (
        <Box display="flex" alignItems="center" sx={{ gap: 0.5 }}>
            <Tooltip
                title={t("workShiftPlanning.colorScheme.button")}
            >
                <IconButton
                    size="small"
                    onClick={() => history.push(`${pathname}/color-coding`)}
                    sx={{
                        color: colorSchemeEnabled ? "primary.main" : "text.disabled",
                    }}
                >
                    <PaletteIcon
                        fontSize="small"
                        sx={{ color: "inherit" }}
                    />
                </IconButton>
            </Tooltip>
            <Tooltip title={t("workShiftPlanning.compact.hideAll")}>
                <IconButton
                    size="small"
                    onClick={() => dispatch(setAllRowsExpanded(false))}
                    color="primary"
                >
                    <UnfoldLessIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <Tooltip title={t("workShiftPlanning.compact.showAll")}>
                <IconButton
                    size="small"
                    onClick={() => dispatch(setAllRowsExpanded(true))}
                    color="primary"
                >
                    <UnfoldMoreIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </Box>
    );

    return (
        <>
            <LoadingIndicator loading={loading} />
            <TimelineGrid
                startDate={timelineStart}
                endDate={timelineEnd}
                addActions={addActions}
                headerActionsNode={headerActions}
            >
                {isEmployeeTab && employees?.map((employee) => {
                    const actualShifts = actualWorkShiftsByEmployee[employee.id] || [];
                    const plannedShifts = sortedGroupedShifts[employee.id] || [];
                    // Only include planned shifts that start within the pay period
                    const filteredPlannedShifts = plannedShifts.filter(ws =>
                        ws.startTime.substring(0, 10) >= timelineStart &&
                        ws.startTime.substring(0, 10) <= timelineEnd
                    );
                    const totalActualMinutes = actualShifts.reduce((sum, ws) => {
                        return sum + Math.max(0, Base.dayjsDiffInMinutes(ws.startTime, ws.endTime));
                    }, 0);
                    const totalPlannedMinutes = filteredPlannedShifts.reduce((sum, ws) => {
                        return sum + Math.max(0, Base.dayjsDiffInMinutes(ws.startTime, ws.endTime));
                    }, 0);

                    // Calculate projected actuals if current pay period is selected
                    let projectedActualMinutes: number | undefined;

                    const isCurrentPayPeriod = Base.isBetween(
                        new Date(), timelineStart, timelineEnd, "day", "[]"
                    );

                    if (isCurrentPayPeriod && totalActualMinutes) {
                        projectedActualMinutes =
                            // Actuals from past days only
                            actualShifts.reduce((sum, ws) => {
                                if (ws.startTime.substring(0, 10) < todayStr) {
                                    return sum + Math.max(0, Base.dayjsDiffInMinutes(ws.startTime, ws.endTime));
                                }
                                return sum;
                            }, 0) +
                            // Planned shifts from today onward, filtered by pay period
                            filteredPlannedShifts.reduce((sum, ws) => {
                                if (ws.startTime.substring(0, 10) >= todayStr) {
                                    return sum + Math.max(0, Base.dayjsDiffInMinutes(ws.startTime, ws.endTime));
                                }
                                return sum;
                            }, 0);
                    }

                    return (
                        <WorkShiftTimelineRow
                            key={employee.id}
                            shifts={sortedGroupedShifts[employee.id]}
                            selectedShiftIds={multiSelect.selectedKeys}
                            selectedCell={cellSelect.selectedCell}
                            onShiftClick={(shiftId: string, event: React.MouseEvent, shift: PlannedWorkShiftDto) =>
                                multiSelect.handleItemClick(shift, event)
                            }
                            targetId={employee.id}
                            label={`${employee.firstName} ${employee.lastName}`}
                            Icon={PersonIcon}
                            onCellSelect={cellSelect.handleCellSelect}
                            totalActualMinutes={isFuturePayPeriod ? undefined : totalActualMinutes}
                            totalPlannedMinutes={totalPlannedMinutes}
                            {...(isCurrentPayPeriod && {
                                projectedActualMinutes
                            })}
                            dayBookings={filteredDayBookingsByEmployeeId?.[employee.id]}
                            dayBookingTypes={dayBookingTypes}
                            employees={employees}
                            timelineStart={timelineStart}
                            timelineEnd={timelineEnd}
                        />
                    );
                })}
                {isVehicleTab && vehicles?.map((vehicle) => {
                    const plannedShifts = sortedGroupedShifts[vehicle.id] || [];
                    // Only include planned shifts that start within the pay period
                    const filteredPlannedShifts = plannedShifts.filter(ws =>
                        ws.startTime.substring(0, 10) >= timelineStart &&
                        ws.startTime.substring(0, 10) <= timelineEnd
                    );
                    const totalPlannedMinutes = filteredPlannedShifts.reduce((sum, ws) => {
                        return sum + Math.max(0, Base.dayjsDiffInMinutes(ws.startTime, ws.endTime));
                    }, 0);
                    return (
                        <WorkShiftTimelineRow
                            key={vehicle.id}
                            shifts={plannedShifts}
                            selectedShiftIds={multiSelect.selectedKeys}
                            selectedCell={cellSelect.selectedCell}
                            onShiftClick={(shiftId: string, event: React.MouseEvent, shift: PlannedWorkShiftDto) =>
                                multiSelect.handleItemClick(shift, event)
                            }
                            targetId={vehicle.id}
                            label={`${vehicle.brand} ${vehicle.registerNumber}`}
                            Icon={LocalShippingOutlinedIcon}
                            onCellSelect={cellSelect.handleCellSelect}
                            totalPlannedMinutes={totalPlannedMinutes}
                            employees={employees}
                            timelineStart={timelineStart}
                            timelineEnd={timelineEnd}
                        />
                    );
                })}
                {isRouteTab && routes?.map((route) => {
                    const plannedShifts = sortedGroupedShifts[route.id] || [];
                    // Only include planned shifts that start within the pay period
                    const filteredPlannedShifts = plannedShifts.filter(ws =>
                        ws.startTime.substring(0, 10) >= timelineStart &&
                        ws.startTime.substring(0, 10) <= timelineEnd
                    );
                    const totalPlannedMinutes = filteredPlannedShifts.reduce((sum, ws) => {
                        return sum + Math.max(0, Base.dayjsDiffInMinutes(ws.startTime, ws.endTime));
                    }, 0);
                    return (
                        <WorkShiftTimelineRow
                            key={route.id}
                            shifts={plannedShifts}
                            selectedShiftIds={multiSelect.selectedKeys}
                            selectedCell={cellSelect.selectedCell}
                            onShiftClick={(shiftId: string, event: React.MouseEvent, shift: PlannedWorkShiftDto) =>
                                multiSelect.handleItemClick(shift, event)
                            }
                            targetId={route.id}
                            label={route.name}
                            Icon={RouteIcon}
                            onCellSelect={cellSelect.handleCellSelect}
                            totalPlannedMinutes={totalPlannedMinutes}
                            employees={employees}
                            timelineStart={timelineStart}
                            timelineEnd={timelineEnd}
                        />
                    );
                })}
                {sortedGroupedShifts["null"]?.length > 0 && (
                    <WorkShiftTimelineRow
                        label={
                            isVehicleTab
                                ? t("workShiftPlanning.noVehicle")
                                : isRouteTab
                                    ? t("workShiftPlanning.noRoute")
                                    : t("workShiftPlanning.noEmployee")
                        }
                        Icon={
                            isVehicleTab
                                ? LocalShippingOutlinedIcon
                                : isRouteTab
                                    ? RouteIcon
                                    : PersonIcon
                        }
                        shifts={sortedGroupedShifts["null"]}
                        selectedShiftIds={multiSelect.selectedKeys}
                        selectedCell={cellSelect.selectedCell}
                        onShiftClick={(shiftId: string, event: React.MouseEvent, shift: PlannedWorkShiftDto) =>
                            multiSelect.handleItemClick(shift, event)
                        }
                        targetId={"null"}
                        dayBookings={undefined}
                        dayBookingTypes={dayBookingTypes}
                        onCellSelect={cellSelect.handleCellSelect}
                        employees={employees}
                        timelineStart={timelineStart}
                        timelineEnd={timelineEnd}
                    />
                )}
            </TimelineGrid>
            <DayBookingDialog
                open={dayBookingDialogOpen}
                onClose={closeDayBookingDialog}
                onSaved={() => {
                    const employeeIds = (employees || [])?.map(e => e.id)?.filter(Boolean);
                    if (employeeIds.length) {
                        void dispatch(fetchDayBookings({ startDate: timelineStart, endDate: timelineEnd, employeeIds }) as any);
                    }
                }}
            />
        </>
    );
};
